import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BusinessSidebar from "./BusinessSidebar";
import BusinessTopBar from "./BusinessTopBar";
import { DashboardGateProvider } from "./DashboardGateContext";
import PermissionGate from "./PermissionGate";
import { businessPath } from "../../config/businessMenu";
import { fetchProject } from "../../api/dashboard.api";
import { clearSession } from "../../utils/auth";
import { isSubscriptionExpired } from "../../utils/subscription";

const BusinessLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [planExpired, setPlanExpired] = useState(null);

  const dashboardGate = useMemo(
    () => ({ planExpired, setPlanExpired }),
    [planExpired],
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setPlanExpired(null);
  }, [projectId]);

  const isDashboard = /(^|\/)dashboard\/?$/.test(location.pathname);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchProject(projectId);
        const project = res?.project || res?.data || res;
        const expired = isSubscriptionExpired(
          project?.planExpiredOn || project?.plan_expired_on || project?.expiresOn,
        );
        if (cancelled) return;
        setPlanExpired(expired);
        if (expired && !isDashboard) {
          navigate(businessPath(projectId, "dashboard"), { replace: true });
        }
      } catch {
        if (!cancelled) setPlanExpired(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, isDashboard, navigate]);

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  return (
    <DashboardGateProvider value={dashboardGate}>
      <div className={`biz-app${mobileNavOpen ? " biz-nav-open" : ""}`}>
        <button
          type="button"
          className="biz-sidebar-overlay"
          aria-label="Close navigation menu"
          onClick={() => setMobileNavOpen(false)}
        />
        <BusinessSidebar
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          onNavClick={() => setMobileNavOpen(false)}
          onLogout={handleLogout}
        />
        <div className={`biz-main${collapsed ? " biz-main-collapsed" : ""}`}>
          <BusinessTopBar
            onMenuToggle={() => setMobileNavOpen((open) => !open)}
            sidebarCollapsed={collapsed}
            onExpandSidebar={() => setCollapsed(false)}
          />
          <div className="biz-content">
            <PermissionGate />
          </div>
        </div>
      </div>
    </DashboardGateProvider>
  );
};

export default BusinessLayout;
